"use client";

import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Check,
  ChevronDown,
  FilePlus2,
  FileText,
  FormInput,
  Hand,
  Highlighter,
  ImageIcon,
  Layers3,
  LoaderCircle,
  Maximize,
  MessageCircle,
  Minus,
  MousePointer2,
  Plus,
  Redo2,
  RotateCw,
  Save,
  Search,
  ShieldCheck,
  Signature,
  Sparkles,
  Trash2,
  Type,
  Undo2,
  Upload,
  X,
} from "lucide-react";
import fontkit from "@pdf-lib/fontkit";
import interItalicUrl from "@fontsource/inter/files/inter-latin-ext-400-italic.woff?url";
import interUrl from "@fontsource/inter/files/inter-latin-ext-400-normal.woff?url";
import { PDFDocument, degrees, rgb } from "pdf-lib";
import {
  ChangeEvent,
  DragEvent,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { LanguageCode, languages, translator, TranslationKey } from "./i18n";
import { MarketingContent, SiteFooter } from "./marketing";

const RENDER_SCALE = 1.25;
type Translate = ReturnType<typeof translator>;

type TextBlock = {
  id: string;
  pageIndex: number;
  text: string;
  originalText: string;
  x: number;
  y: number;
  width: number;
  height: number;
  originalX?: number;
  originalY?: number;
  originalWidth?: number;
  originalHeight?: number;
  fontSize: number;
  originalFontSize: number;
  fontFamily: string;
  detectedFont: string;
  color: string;
  modified: boolean;
  inserted?: boolean;
};

type RenderedPage = {
  index: number;
  width: number;
  height: number;
  image: string;
  blocks: TextBlock[];
  added?: boolean;
};

type Redaction = { pageIndex: number; x: number; y: number; width: number; height: number };
type SignatureMark = { pageIndex: number; text: string; x: number; y: number };
type ImageMark = {
  id: string;
  pageIndex: number;
  src: string;
  mimeType: "image/png" | "image/jpeg";
  x: number;
  y: number;
  width: number;
  height: number;
};

type EditorSnapshot = {
  pages: RenderedPage[];
  imageMarks: ImageMark[];
  redactions: Redaction[];
  signatures: SignatureMark[];
  deletedPages: number[];
  rotations: Record<number, number>;
  activePage: number;
};

const tools: { id: string; label: TranslationKey; icon: typeof Type }[] = [
  { id: "text", label: "text", icon: Type },
  { id: "image", label: "image", icon: ImageIcon },
  { id: "sign", label: "sign", icon: Signature },
  { id: "comment", label: "comment", icon: MessageCircle },
  { id: "redact", label: "redact", icon: Highlighter },
  { id: "form", label: "form", icon: FormInput },
  { id: "organize", label: "organize", icon: Layers3 },
];

function createDemoPage(): RenderedPage {
  return { index: 0, width: 650, height: 920, image: "", blocks: [] };
}

function cleanFontName(value: string) {
  return value.replace(/^[A-Z]{6}\+/, "").replace(/["']/g, "") || "Czcionka osadzona";
}

function fontConfidence(font: string) {
  const lowered = font.toLowerCase();
  if (lowered.includes("inter") || lowered.includes("helvetica") || lowered.includes("arial")) return 98;
  if (lowered.includes("times") || lowered.includes("serif")) return 94;
  if (lowered.includes("courier") || lowered.includes("mono")) return 92;
  return 87;
}

function hexToRgb(value: string) {
  const normalized = value.replace("#", "");
  const safe = /^[0-9a-fA-F]{6}$/.test(normalized) ? normalized : "111827";
  return rgb(
    Number.parseInt(safe.slice(0, 2), 16) / 255,
    Number.parseInt(safe.slice(2, 4), 16) / 255,
    Number.parseInt(safe.slice(4, 6), 16) / 255,
  );
}

function downloadBlob(bytes: Uint8Array, name: string) {
  const copy = new Uint8Array(bytes.length);
  copy.set(bytes);
  const url = URL.createObjectURL(new Blob([copy.buffer], { type: "application/pdf" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name.replace(/\.pdf$/i, "") + "_PDFix.pdf";
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

function readImageFile(file: File) {
  return new Promise<{ src: string; width: number; height: number }>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const src = String(reader.result);
      const image = new Image();
      image.onerror = () => reject(new Error("Could not read image dimensions."));
      image.onload = () => resolve({ src, width: image.naturalWidth, height: image.naturalHeight });
      image.src = src;
    };
    reader.readAsDataURL(file);
  });
}

const featureNotices: Record<LanguageCode, {
  placeText: string;
  textAdded: string;
  imageAdded: string;
  imageError: string;
}> = {
  en: { placeText: "Click an empty place on the PDF to add text.", textAdded: "Text added. Edit it in the panel on the right.", imageAdded: "Image added. Drag it to move; use the corner handle to resize.", imageError: "The image could not be added. Choose a PNG or JPEG file." },
  de: { placeText: "Klicken Sie auf eine freie Stelle im PDF, um Text hinzuzufügen.", textAdded: "Text hinzugefügt. Bearbeiten Sie ihn rechts im Bedienfeld.", imageAdded: "Bild hinzugefügt. Ziehen zum Verschieben; Eckgriff zum Skalieren.", imageError: "Das Bild konnte nicht hinzugefügt werden. Wählen Sie PNG oder JPEG." },
  es: { placeText: "Haz clic en un lugar vacío del PDF para añadir texto.", textAdded: "Texto añadido. Edítalo en el panel derecho.", imageAdded: "Imagen añadida. Arrástrala para moverla y usa la esquina para cambiar su tamaño.", imageError: "No se pudo añadir la imagen. Elige un archivo PNG o JPEG." },
  fr: { placeText: "Cliquez sur une zone vide du PDF pour ajouter du texte.", textAdded: "Texte ajouté. Modifiez-le dans le panneau de droite.", imageAdded: "Image ajoutée. Faites-la glisser et utilisez la poignée d’angle pour la redimensionner.", imageError: "Impossible d’ajouter l’image. Choisissez un fichier PNG ou JPEG." },
  it: { placeText: "Fai clic su uno spazio vuoto del PDF per aggiungere testo.", textAdded: "Testo aggiunto. Modificalo nel pannello a destra.", imageAdded: "Immagine aggiunta. Trascinala per spostarla e usa l’angolo per ridimensionarla.", imageError: "Impossibile aggiungere l’immagine. Scegli un file PNG o JPEG." },
  pt: { placeText: "Clique num espaço vazio do PDF para adicionar texto.", textAdded: "Texto adicionado. Edite-o no painel à direita.", imageAdded: "Imagem adicionada. Arraste para mover e use o canto para redimensionar.", imageError: "Não foi possível adicionar a imagem. Escolha um ficheiro PNG ou JPEG." },
  nl: { placeText: "Klik op een lege plek in de PDF om tekst toe te voegen.", textAdded: "Tekst toegevoegd. Bewerk deze in het rechterpaneel.", imageAdded: "Afbeelding toegevoegd. Sleep om te verplaatsen en gebruik de hoek om het formaat te wijzigen.", imageError: "De afbeelding kon niet worden toegevoegd. Kies een PNG- of JPEG-bestand." },
  pl: { placeText: "Kliknij puste miejsce w PDF, aby dodać tekst.", textAdded: "Dodano tekst. Edytuj go w panelu po prawej.", imageAdded: "Dodano obraz. Przeciągnij go, aby przesunąć; użyj narożnika, aby zmienić rozmiar.", imageError: "Nie udało się dodać obrazu. Wybierz plik PNG lub JPEG." },
};

export default function Home() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const documentStageRef = useRef<HTMLElement>(null);
  const [language, setLanguage] = useState<LanguageCode>(() => {
    if (typeof window === "undefined") return "en";
    const saved = window.localStorage.getItem("pdfix-language") as LanguageCode | null;
    return saved && languages.some((item) => item.code === saved) ? saved : "en";
  });
  const t = useMemo(() => translator(language), [language]);
  const [pages, setPages] = useState<RenderedPage[]>([createDemoPage()]);
  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);
  const [fileName, setFileName] = useState("Faktura_lipiec.pdf");
  const [activePage, setActivePage] = useState(0);
  const [activeTool, setActiveTool] = useState("text");
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(100);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [query, setQuery] = useState("");
  const [notice, setNotice] = useState(translator("en")("demo"));
  const [undoStack, setUndoStack] = useState<EditorSnapshot[]>([]);
  const [redoStack, setRedoStack] = useState<EditorSnapshot[]>([]);
  const [redactions, setRedactions] = useState<Redaction[]>([]);
  const [signatures, setSignatures] = useState<SignatureMark[]>([]);
  const [imageMarks, setImageMarks] = useState<ImageMark[]>([]);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [textInsertArmed, setTextInsertArmed] = useState(false);
  const [deletedPages, setDeletedPages] = useState<number[]>([]);
  const [rotations, setRotations] = useState<Record<number, number>>({});
  const [signatureDialog, setSignatureDialog] = useState(false);
  const [signatureText, setSignatureText] = useState("");
  const [editorAttention, setEditorAttention] = useState(0);
  const [stageWidth, setStageWidth] = useState(0);

  const currentPage = pages[activePage] ?? pages[0];
  const selectedBlock = currentPage?.blocks.find((block) => block.id === selectedBlockId) ?? null;
  const confidence = selectedBlock ? fontConfidence(selectedBlock.detectedFont) : 98;
  const modifiedCount = pages.reduce((sum, page) => sum + page.blocks.filter((block) => block.modified).length, 0);
  const currentRotation = rotations[activePage] ?? 0;
  const requestedScale = zoom / 100;
  const rotatedPageWidth = currentRotation % 180 === 0 ? currentPage.width : currentPage.height;
  const rotatedPageHeight = currentRotation % 180 === 0 ? currentPage.height : currentPage.width;
  const availableStageWidth = stageWidth > 0 ? Math.max(180, stageWidth) : rotatedPageWidth;
  const displayScale = Math.min(requestedScale, availableStageWidth / rotatedPageWidth);
  const displayedPageWidth = rotatedPageWidth * displayScale;
  const displayedPageHeight = rotatedPageHeight * displayScale;

  useEffect(() => {
    document.documentElement.lang = language;
    window.localStorage.setItem("pdfix-language", language);
  }, [language]);

  useEffect(() => {
    const stage = documentStageRef.current;
    if (!stage) return;
    const updateWidth = () => {
      const styles = window.getComputedStyle(stage);
      const horizontalPadding = Number.parseFloat(styles.paddingLeft) + Number.parseFloat(styles.paddingRight);
      setStageWidth(Math.max(0, stage.clientWidth - horizontalPadding));
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  const changeLanguage = (next: LanguageCode) => {
    setLanguage(next);
    if (!pdfBytes) setNotice(translator(next)("demo"));
  };

  const captureSnapshot = (): EditorSnapshot => ({
    pages,
    imageMarks,
    redactions,
    signatures,
    deletedPages,
    rotations,
    activePage,
  });

  const pushHistory = () => {
    const snapshot = captureSnapshot();
    setUndoStack((items) => [...items.slice(-39), snapshot]);
    setRedoStack([]);
  };

  const restoreSnapshot = (snapshot: EditorSnapshot) => {
    setPages(snapshot.pages);
    setImageMarks(snapshot.imageMarks);
    setRedactions(snapshot.redactions);
    setSignatures(snapshot.signatures);
    setDeletedPages(snapshot.deletedPages);
    setRotations(snapshot.rotations);
    setActivePage(snapshot.activePage);
    setSelectedBlockId(null);
    setSelectedImageId(null);
    setTextInsertArmed(false);
  };

  const loadPdf = useCallback(async (file: File) => {
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setNotice(t("selectPdf"));
      return;
    }
    setLoading(true);
    setSelectedBlockId(null);
    setSelectedImageId(null);
    setTextInsertArmed(false);
    setNotice(t("analysing"));
    try {
      const source = new Uint8Array(await file.arrayBuffer());
      const pdfjs = await import("pdfjs-dist");
      pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
      const loadingTask = pdfjs.getDocument({ data: source.slice() });
      const pdfDocument = await loadingTask.promise;
      const rendered: RenderedPage[] = [];

      for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
        const page = await pdfDocument.getPage(pageNumber);
        const viewport = page.getViewport({ scale: RENDER_SCALE });
        const canvas = document.createElement("canvas");
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        const context = canvas.getContext("2d", { alpha: false });
        if (!context) throw new Error(t("previewError"));
        await page.render({ canvasContext: context, viewport, canvas }).promise;

        const textContent = await page.getTextContent();
        const styles = textContent.styles as Record<string, { fontFamily?: string }>;
        const blocks: TextBlock[] = [];
        textContent.items.forEach((raw, itemIndex) => {
          if (!("str" in raw) || !raw.str.trim()) return;
          const item = raw as typeof raw & {
            str: string;
            transform: number[];
            width: number;
            height: number;
            fontName: string;
          };
          const matrix = pdfjs.Util.transform(viewport.transform, item.transform);
          const renderedFontSize = Math.max(6, Math.hypot(matrix[2], matrix[3]));
          const family = cleanFontName(styles[item.fontName]?.fontFamily || item.fontName);
          const x = matrix[4];
          const y = matrix[5] - renderedFontSize;
          const width = Math.max(item.width * RENDER_SCALE, 8);
          const height = Math.max(renderedFontSize * 1.15, 8);
          blocks.push({
            id: `${pageNumber - 1}-${itemIndex}`,
            pageIndex: pageNumber - 1,
            text: item.str,
            originalText: item.str,
            x,
            y,
            width,
            height,
            originalX: x,
            originalY: y,
            originalWidth: width,
            originalHeight: height,
            fontSize: renderedFontSize / RENDER_SCALE,
            originalFontSize: renderedFontSize / RENDER_SCALE,
            fontFamily: family,
            detectedFont: family,
            color: "#111827",
            modified: false,
          });
        });
        rendered.push({
          index: pageNumber - 1,
          width: viewport.width,
          height: viewport.height,
          image: canvas.toDataURL("image/jpeg", 0.94),
          blocks,
        });
      }

      setPdfBytes(source);
      setPages(rendered);
      setFileName(file.name);
      setActivePage(0);
      setUndoStack([]);
      setRedoStack([]);
      setRedactions([]);
      setSignatures([]);
      setImageMarks([]);
      setSelectedImageId(null);
      setDeletedPages([]);
      setRotations({});
      const blockCount = rendered.reduce((sum, page) => sum + page.blocks.length, 0);
      setNotice(blockCount
        ? t("found", { blocks: blockCount, fonts: new Set(rendered.flatMap((page) => page.blocks.map((block) => block.detectedFont))).size })
        : t("scan"));
    } catch (error) {
      console.error(error);
      setNotice(t("readError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) void loadPdf(file);
    event.target.value = "";
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) void loadPdf(file);
  };

  const commitBlockChange = (
    block: TextBlock,
    patch: Partial<Pick<TextBlock, "text" | "fontSize" | "fontFamily" | "color" | "width" | "height">>,
  ) => {
    const before = {
      text: block.text,
      fontSize: block.fontSize,
      fontFamily: block.fontFamily,
      color: block.color,
      width: block.width,
      height: block.height,
      modified: block.modified,
    };
    const after = {
      text: patch.text ?? block.text,
      fontSize: patch.fontSize ?? block.fontSize,
      fontFamily: patch.fontFamily ?? block.fontFamily,
      color: patch.color ?? block.color,
      width: patch.width ?? block.width,
      height: patch.height ?? block.height,
      modified: true,
    };
    if (
      before.text === after.text
      && before.fontSize === after.fontSize
      && before.fontFamily === after.fontFamily
      && before.color === after.color
      && before.width === after.width
      && before.height === after.height
    ) return;
    pushHistory();
    setPages((allPages) => allPages.map((page) => page.index === block.pageIndex
      ? { ...page, blocks: page.blocks.map((item) => item.id === block.id ? { ...item, ...after } : item) }
      : page));
  };

  const undo = () => {
    const previous = undoStack[undoStack.length - 1];
    if (!previous) return;
    const current = captureSnapshot();
    setUndoStack((items) => items.slice(0, -1));
    setRedoStack((items) => [current, ...items].slice(0, 40));
    restoreSnapshot(previous);
  };

  const redo = () => {
    const next = redoStack[0];
    if (!next) return;
    const current = captureSnapshot();
    setRedoStack((items) => items.slice(1));
    setUndoStack((items) => [...items.slice(-39), current]);
    restoreSnapshot(next);
  };

  const screenDeltaToPage = (deltaX: number, deltaY: number) => {
    const angle = ((currentRotation % 360) + 360) % 360;
    if (angle === 90) return { x: deltaY / displayScale, y: -deltaX / displayScale };
    if (angle === 180) return { x: -deltaX / displayScale, y: -deltaY / displayScale };
    if (angle === 270) return { x: -deltaY / displayScale, y: deltaX / displayScale };
    return { x: deltaX / displayScale, y: deltaY / displayScale };
  };

  const startTextDrag = (event: ReactPointerEvent<HTMLButtonElement>, block: TextBlock) => {
    if (activeTool === "redact" || activeTool === "comment") return;
    if ((event.target as HTMLElement).classList.contains("overlay-resize-handle")) return;
    event.preventDefault();
    event.stopPropagation();
    setActiveTool("text");
    setSelectedBlockId(block.id);
    setSelectedImageId(null);
    setTextInsertArmed(false);
    setEditorAttention((value) => value + 1);
    const startX = event.clientX;
    const startY = event.clientY;
    let historySaved = false;

    const onMove = (pointerEvent: PointerEvent) => {
      if (!historySaved) {
        pushHistory();
        historySaved = true;
      }
      const delta = screenDeltaToPage(pointerEvent.clientX - startX, pointerEvent.clientY - startY);
      const x = Math.max(0, Math.min(currentPage.width - block.width, block.x + delta.x));
      const y = Math.max(0, Math.min(currentPage.height - block.height, block.y + delta.y));
      setPages((allPages) => allPages.map((page) => page.index === block.pageIndex
        ? { ...page, blocks: page.blocks.map((item) => item.id === block.id ? { ...item, x, y, modified: true } : item) }
        : page));
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
  };

  const startTextResize = (event: ReactPointerEvent<HTMLSpanElement>, block: TextBlock) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startY = event.clientY;
    let historySaved = false;

    const onMove = (pointerEvent: PointerEvent) => {
      if (!historySaved) {
        pushHistory();
        historySaved = true;
      }
      const delta = screenDeltaToPage(pointerEvent.clientX - startX, pointerEvent.clientY - startY);
      const width = Math.max(24, Math.min(currentPage.width - block.x, block.width + delta.x));
      const height = Math.max(12, Math.min(currentPage.height - block.y, block.height + delta.y));
      setPages((allPages) => allPages.map((page) => page.index === block.pageIndex
        ? { ...page, blocks: page.blocks.map((item) => item.id === block.id ? { ...item, width, height, modified: true } : item) }
        : page));
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
  };

  const handlePageClick = (event: ReactMouseEvent<HTMLElement>) => {
    if (activeTool !== "text" || !textInsertArmed || !pdfBytes) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const screenX = event.clientX - bounds.left;
    const screenY = event.clientY - bounds.top;
    const angle = ((currentRotation % 360) + 360) % 360;
    let pageX = screenX / displayScale;
    let pageY = screenY / displayScale;
    if (angle === 90) {
      pageX = screenY / displayScale;
      pageY = currentPage.height - screenX / displayScale;
    } else if (angle === 180) {
      pageX = currentPage.width - screenX / displayScale;
      pageY = currentPage.height - screenY / displayScale;
    } else if (angle === 270) {
      pageX = currentPage.width - screenY / displayScale;
      pageY = screenX / displayScale;
    }
    const x = Math.max(0, Math.min(currentPage.width - 180, pageX));
    const y = Math.max(0, Math.min(currentPage.height - 28, pageY));
    const block: TextBlock = {
      id: `added-text-${Date.now()}`,
      pageIndex: activePage,
      text: t("text"),
      originalText: "",
      x,
      y,
      width: 180,
      height: 28,
      fontSize: 16,
      originalFontSize: 16,
      fontFamily: "Inter",
      detectedFont: "Inter",
      color: "#111827",
      modified: true,
      inserted: true,
    };
    pushHistory();
    setPages((allPages) => allPages.map((page) => page.index === activePage
      ? { ...page, blocks: [...page.blocks, block] }
      : page));
    setSelectedImageId(null);
    setSelectedBlockId(block.id);
    setTextInsertArmed(false);
    setEditorAttention((value) => value + 1);
    setNotice(featureNotices[language].textAdded);
  };

  const startImageDrag = (event: ReactPointerEvent<HTMLDivElement>, mark: ImageMark) => {
    if ((event.target as HTMLElement).classList.contains("overlay-resize-handle")) return;
    event.preventDefault();
    event.stopPropagation();
    setSelectedImageId(mark.id);
    setSelectedBlockId(null);
    const startX = event.clientX;
    const startY = event.clientY;
    let historySaved = false;

    const onMove = (pointerEvent: PointerEvent) => {
      if (!historySaved) {
        pushHistory();
        historySaved = true;
      }
      const delta = screenDeltaToPage(pointerEvent.clientX - startX, pointerEvent.clientY - startY);
      const x = Math.max(0, Math.min(currentPage.width - mark.width, mark.x + delta.x));
      const y = Math.max(0, Math.min(currentPage.height - mark.height, mark.y + delta.y));
      setImageMarks((items) => items.map((item) => item.id === mark.id ? { ...item, x, y } : item));
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
  };

  const startImageResize = (event: ReactPointerEvent<HTMLSpanElement>, mark: ImageMark) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedImageId(mark.id);
    const startX = event.clientX;
    const startY = event.clientY;
    const ratio = mark.width / mark.height;
    let historySaved = false;

    const onMove = (pointerEvent: PointerEvent) => {
      if (!historySaved) {
        pushHistory();
        historySaved = true;
      }
      const delta = screenDeltaToPage(pointerEvent.clientX - startX, pointerEvent.clientY - startY);
      const widthFromX = mark.width + delta.x;
      const heightFromY = mark.height + delta.y;
      const maximumWidth = Math.min(currentPage.width - mark.x, (currentPage.height - mark.y) * ratio);
      const width = Math.max(32, Math.min(maximumWidth, Math.max(widthFromX, heightFromY * ratio)));
      const height = width / ratio;
      setImageMarks((items) => items.map((item) => item.id === mark.id ? { ...item, width, height } : item));
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
  };

  const handleBlockClick = (block: TextBlock) => {
    if (activeTool === "redact") {
      pushHistory();
      setRedactions((items) => items.some((item) => item.pageIndex === block.pageIndex && item.x === block.x && item.y === block.y)
        ? items
        : [...items, { pageIndex: block.pageIndex, x: block.x, y: block.y, width: block.width, height: block.height }]);
      setNotice(t("redactAdded"));
      return;
    }
    if (activeTool === "comment") {
      setNotice(`Adnotacja przypięta do tekstu „${block.text.slice(0, 28)}${block.text.length > 28 ? "…" : ""}”`);
      setSelectedBlockId(block.id);
      return;
    }
    setActiveTool("text");
    setTextInsertArmed(false);
    setSelectedImageId(null);
    setSelectedBlockId(block.id);
    setEditorAttention((value) => value + 1);
  };

  const handleTool = (id: string) => {
    setActiveTool(id);
    if (id === "text") {
      setTextInsertArmed(true);
      setSelectedBlockId(null);
      setSelectedImageId(null);
      setNotice(featureNotices[language].placeText);
    }
    if (id === "image") {
      setTextInsertArmed(false);
      setSelectedBlockId(null);
      imageInputRef.current?.click();
    }
    if (id === "sign") setSignatureDialog(true);
    if (id === "form") setNotice(t("formMode"));
  };

  const addSignature = () => {
    if (!signatureText.trim()) return;
    pushHistory();
    setSignatures((items) => [...items, {
      pageIndex: activePage,
      text: signatureText.trim(),
      x: currentPage.width * 0.64,
      y: currentPage.height * 0.86,
    }]);
    setSignatureDialog(false);
    setSignatureText("");
    setNotice(t("signAdded"));
  };

  const onImagePicked = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !pdfBytes) return;
    if (file.type !== "image/png" && file.type !== "image/jpeg") {
      setNotice(featureNotices[language].imageError);
      return;
    }
    try {
      const image = await readImageFile(file);
      const width = Math.min(260, currentPage.width * 0.42, image.width);
      const height = width * (image.height / image.width);
      const mark: ImageMark = {
        id: `image-${Date.now()}`,
        pageIndex: activePage,
        src: image.src,
        mimeType: file.type,
        x: Math.max(0, (currentPage.width - width) / 2),
        y: Math.max(0, (currentPage.height - height) / 2),
        width,
        height,
      };
      pushHistory();
      setImageMarks((items) => [...items, mark]);
      setSelectedImageId(mark.id);
      setSelectedBlockId(null);
      setNotice(featureNotices[language].imageAdded);
    } catch (error) {
      console.error(error);
      setNotice(featureNotices[language].imageError);
    }
  };

  const addBlankPage = () => {
    if (!pdfBytes) {
      fileInputRef.current?.click();
      return;
    }
    const pageIndex = pages.reduce((maximum, page) => Math.max(maximum, page.index), -1) + 1;
    const page: RenderedPage = {
      index: pageIndex,
      width: currentPage.width,
      height: currentPage.height,
      image: "",
      blocks: [],
      added: true,
    };
    pushHistory();
    setPages((items) => [...items, page]);
    setActivePage(pageIndex);
    setSelectedBlockId(null);
    setSelectedImageId(null);
    setTextInsertArmed(false);
    setNotice(t("addPage"));
  };

  const rotateActivePage = () => {
    pushHistory();
    setRotations((items) => ({ ...items, [activePage]: ((items[activePage] ?? 0) + 90) % 360 }));
  };

  const exportPdf = async () => {
    if (!pdfBytes) {
      fileInputRef.current?.click();
      return;
    }
    setSaving(true);
    setNotice(t("saveStart"));
    try {
      const pdf = await PDFDocument.load(pdfBytes.slice());
      const needsTextFont = modifiedCount > 0;
      const needsSignatureFont = signatures.length > 0;
      let unicodeFont: Awaited<ReturnType<typeof pdf.embedFont>> | null = null;
      let signatureFont: Awaited<ReturnType<typeof pdf.embedFont>> | null = null;

      if (needsTextFont || needsSignatureFont) {
        pdf.registerFontkit(fontkit);
        const responses = await Promise.all([
          needsTextFont ? fetch(interUrl) : Promise.resolve(null),
          needsSignatureFont ? fetch(interItalicUrl) : Promise.resolve(null),
        ]);
        responses.forEach((response) => {
          if (response && !response.ok) throw new Error(`Font download failed (${response.status})`);
        });
        if (responses[0]) unicodeFont = await pdf.embedFont(new Uint8Array(await responses[0].arrayBuffer()), { subset: true });
        if (responses[1]) signatureFont = await pdf.embedFont(new Uint8Array(await responses[1].arrayBuffer()), { subset: true });
      }

      pages
        .filter((page) => page.added)
        .sort((first, second) => first.index - second.index)
        .forEach((page) => {
          while (pdf.getPageCount() <= page.index) {
            pdf.addPage([page.width / RENDER_SCALE, page.height / RENDER_SCALE]);
          }
        });

      pages.forEach((renderedPage) => {
        const page = pdf.getPage(renderedPage.index);
        const pageHeight = page.getHeight();
        renderedPage.blocks.filter((block) => block.modified).forEach((block) => {
          const x = block.x / RENDER_SCALE;
          const width = Math.max(block.width / RENDER_SCALE, 5);
          const height = Math.max(block.height / RENDER_SCALE, block.fontSize * 1.15);
          const y = pageHeight - (block.y / RENDER_SCALE) - height;
          if (!block.inserted) {
            const originalX = (block.originalX ?? block.x) / RENDER_SCALE;
            const originalWidth = Math.max((block.originalWidth ?? block.width) / RENDER_SCALE, 5);
            const originalHeight = Math.max((block.originalHeight ?? block.height) / RENDER_SCALE, block.originalFontSize * 1.15);
            const originalY = pageHeight - ((block.originalY ?? block.y) / RENDER_SCALE) - originalHeight;
            page.drawRectangle({
              x: originalX - 1,
              y: originalY - 1,
              width: originalWidth + 3,
              height: originalHeight + 3,
              color: rgb(1, 1, 1),
            });
          }
          page.drawText(block.text, {
            x,
            y: y + Math.max(1, height - block.fontSize),
            size: block.fontSize,
            font: unicodeFont!,
            color: hexToRgb(block.color),
            maxWidth: Math.max(width * 2.5, 90),
          });
        });
      });

      const embeddedImages = await Promise.all(imageMarks.map(async (mark) => {
        const bytes = new Uint8Array(await (await fetch(mark.src)).arrayBuffer());
        const image = mark.mimeType === "image/png" ? await pdf.embedPng(bytes) : await pdf.embedJpg(bytes);
        return { mark, image };
      }));
      embeddedImages.forEach(({ mark, image }) => {
        const page = pdf.getPage(mark.pageIndex);
        const pageHeight = page.getHeight();
        page.drawImage(image, {
          x: mark.x / RENDER_SCALE,
          y: pageHeight - (mark.y + mark.height) / RENDER_SCALE,
          width: mark.width / RENDER_SCALE,
          height: mark.height / RENDER_SCALE,
        });
      });

      redactions.forEach((mark) => {
        const page = pdf.getPage(mark.pageIndex);
        const pageHeight = page.getHeight();
        page.drawRectangle({
          x: mark.x / RENDER_SCALE,
          y: pageHeight - (mark.y + mark.height) / RENDER_SCALE,
          width: mark.width / RENDER_SCALE,
          height: mark.height / RENDER_SCALE,
          color: rgb(0.035, 0.043, 0.059),
        });
      });

      signatures.forEach((mark) => {
        const page = pdf.getPage(mark.pageIndex);
        const pageHeight = page.getHeight();
        page.drawText(mark.text, {
          x: mark.x / RENDER_SCALE,
          y: pageHeight - mark.y / RENDER_SCALE,
          size: 18,
          font: signatureFont!,
          color: rgb(0.08, 0.12, 0.22),
        });
      });

      Object.entries(rotations).forEach(([pageIndex, angle]) => {
        pdf.getPage(Number(pageIndex)).setRotation(degrees(angle));
      });
      [...deletedPages].sort((a, b) => b - a).forEach((pageIndex) => {
        if (pdf.getPageCount() > 1) pdf.removePage(pageIndex);
      });

      const output = await pdf.save();
      downloadBlob(output, fileName);
      setNotice(t("saved", { changes: modifiedCount, redactions: redactions.length, signatures: signatures.length }));
    } catch (error) {
      console.error(error);
      setNotice(t("saveError"));
    } finally {
      setSaving(false);
    }
  };

  const visibleBlocks = useMemo(() => currentPage?.blocks.filter((block) => !query || block.text.toLowerCase().includes(query.toLowerCase())) ?? [], [currentPage, query]);
  const visiblePages = useMemo(() => pages.filter((page) => !deletedPages.includes(page.index)), [pages, deletedPages]);

  return (
    <>
    <MarketingContent language={language} onLanguageChange={changeLanguage} />
    <main
      id="pdf-editor"
      className={`app-shell ${dragging ? "is-dragging" : ""}`}
      onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
      onDragLeave={(event) => { if (event.currentTarget === event.target) setDragging(false); }}
      onDrop={onDrop}
    >
      <input ref={fileInputRef} className="visually-hidden" type="file" accept="application/pdf,.pdf" onChange={onFileChange} />
      <input ref={imageInputRef} className="visually-hidden" type="file" accept="image/png,image/jpeg" onChange={onImagePicked} />

      <header className="topbar">
        <div className="brand"><span className="brand-mark"><Sparkles size={18} /></span><span>PDFix</span></div>
        <button className="filename" onClick={() => fileInputRef.current?.click()}>{fileName} <ChevronDown size={14} /></button>
        <div className="privacy"><ShieldCheck size={15} /> {t("local")}</div>
        <label className="language-picker" title={t("language")}>
          <span>{language.toUpperCase()}</span>
          <select value={language} onChange={(event) => changeLanguage(event.target.value as LanguageCode)} aria-label={t("language")}>
            {languages.map((item) => <option key={item.code} value={item.code}>{item.label}</option>)}
          </select>
        </label>
        <button className="open-button" onClick={() => fileInputRef.current?.click()}><Upload size={16} /> {t("open")}</button>
        <button className="save-button" onClick={() => void exportPdf()} disabled={saving}>
          {saving ? <LoaderCircle className="spin" size={17} /> : <Save size={17} />}
          {saving ? t("saving") : t("save")} <ChevronDown size={15} />
        </button>
      </header>

      <section className="toolbar">
        <div className="history-tools">
          <button className="tool-icon" aria-label={t("undo")} onClick={undo} disabled={undoStack.length === 0}><Undo2 size={20} /></button>
          <button className="tool-icon" aria-label={t("redo")} onClick={redo} disabled={redoStack.length === 0}><Redo2 size={20} /></button>
        </div>
        <div className="main-tools">
          {tools.map(({ id, label, icon: Icon }) => (
            <button
              className={`tool-button ${activeTool === id ? "active" : ""}`}
              key={id}
              onClick={() => handleTool(id)}
              aria-pressed={activeTool === id}
            >
              <Icon size={19} /><span>{t(label)}</span>
            </button>
          ))}
        </div>
        <div className="view-tools">
          <button className="tool-icon active-subtle" aria-label="Przesuwanie"><Hand size={19} /></button>
          <button className="tool-icon" aria-label="Zaznaczanie"><MousePointer2 size={19} /></button>
          <span className="divider" />
          <button className="zoom-button" onClick={() => setZoom(Math.max(50, zoom - 10))}><Minus size={16} /></button>
          <span className="zoom-value">{zoom}%</span>
          <button className="zoom-button" onClick={() => setZoom(Math.min(200, zoom + 10))}><Plus size={16} /></button>
          <button className="tool-icon" aria-label="Dopasuj do ekranu" onClick={() => setZoom(100)}><Maximize size={18} /></button>
        </div>
      </section>

      <section className="workspace">
        <aside className="pages-panel">
          <div className="panel-heading"><span>{t("pages")} <b>{visiblePages.length}</b></span><Search size={16} /></div>
          <div className="search-box"><Search size={14} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("find")} aria-label={t("find")} /></div>
          {visiblePages.map((page, listIndex) => (
            <button className={`thumbnail-wrap ${page.index === activePage ? "selected" : ""}`} key={page.index} onClick={() => { setActivePage(page.index); setSelectedBlockId(null); setSelectedImageId(null); }}>
              {page.image
                ? <img className="thumbnail real-thumb" src={page.image} alt={`Miniatura strony ${listIndex + 1}`} />
                : page.added ? <BlankThumbnail /> : <DemoThumbnail />}
              <span>{listIndex + 1}</span>
            </button>
          ))}
          <button className="add-page" onClick={addBlankPage}><FilePlus2 size={17} /> {t("addPage")}</button>
        </aside>

        <section
          ref={documentStageRef}
          className="document-stage"
          style={{ minHeight: pdfBytes ? displayedPageHeight + 204 : undefined }}
        >
          <div className="stage-status">
            {loading ? <LoaderCircle className="spin" size={13} /> : <span className={`status-dot ${pdfBytes ? "" : "demo"}`} />}
            {notice}
          </div>

          {loading ? (
            <div className="loading-card"><LoaderCircle className="spin" size={32} /><strong>{t("analyseDoc")}</strong><span>{t("analyseDetails")}</span></div>
          ) : !pdfBytes ? (
            <DemoInvoice zoom={zoom} onOpen={() => fileInputRef.current?.click()} t={t} />
          ) : (
            <div
              className="page-transform-frame"
              style={{ width: displayedPageWidth, height: displayedPageHeight }}
            >
              <article
                className={`rendered-page ${textInsertArmed ? "text-insert-armed" : ""}`}
                onClick={handlePageClick}
                style={{
                  width: currentPage.width,
                  height: currentPage.height,
                  transform: `translate(-50%, -50%) rotate(${currentRotation}deg) scale(${displayScale})`,
                }}
              >
                {currentPage.image && <img src={currentPage.image} alt={`Strona ${activePage + 1} dokumentu ${fileName}`} draggable={false} />}
                <div className="text-layer">
                  {visibleBlocks.filter((block) => block.modified && !block.inserted).map((block) => (
                    <span
                      className="original-text-cover"
                      key={`cover-${block.id}`}
                      style={{
                        left: block.originalX ?? block.x,
                        top: block.originalY ?? block.y,
                        width: block.originalWidth ?? block.width,
                        height: block.originalHeight ?? block.height,
                      }}
                    />
                  ))}
                  {visibleBlocks.map((block) => (
                    <button
                      key={block.id}
                      className={`pdf-text-block ${selectedBlockId === block.id ? "selected" : ""} ${block.modified ? "modified" : ""} ${block.inserted ? "inserted" : ""}`}
                      style={{
                        left: block.x,
                        top: block.y,
                        width: Math.max(block.width, 8),
                        height: Math.max(block.height, 8),
                        fontSize: block.fontSize * RENDER_SCALE,
                        fontFamily: block.fontFamily,
                        color: block.modified ? block.color : "transparent",
                      }}
                      title={`${block.detectedFont} · ${Math.round(block.fontSize)} px`}
                      onPointerDown={(event) => startTextDrag(event, block)}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (activeTool === "redact" || activeTool === "comment") handleBlockClick(block);
                      }}
                    >
                      {block.modified ? block.text : ""}
                      {selectedBlockId === block.id && (
                        <span
                          className="overlay-resize-handle text-resize-handle"
                          role="button"
                          aria-label="Resize selected text area"
                          onPointerDown={(event) => startTextResize(event, block)}
                        />
                      )}
                    </button>
                  ))}
                </div>
                {imageMarks.filter((mark) => mark.pageIndex === activePage).map((mark) => (
                  <div
                    className={`image-mark ${selectedImageId === mark.id ? "selected" : ""}`}
                    key={mark.id}
                    style={{ left: mark.x, top: mark.y, width: mark.width, height: mark.height }}
                    onPointerDown={(event) => startImageDrag(event, mark)}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <img src={mark.src} alt="" draggable={false} />
                    {selectedImageId === mark.id && (
                      <>
                        <button
                          className="image-remove-button"
                          type="button"
                          aria-label="Remove image"
                          onPointerDown={(event) => event.stopPropagation()}
                          onClick={(event) => {
                            event.stopPropagation();
                            pushHistory();
                            setImageMarks((items) => items.filter((item) => item.id !== mark.id));
                            setSelectedImageId(null);
                          }}
                        >
                          <X size={14} />
                        </button>
                        <span
                          className="overlay-resize-handle image-resize-handle"
                          role="button"
                          aria-label="Resize image"
                          onPointerDown={(event) => startImageResize(event, mark)}
                        />
                      </>
                    )}
                  </div>
                ))}
                {redactions.filter((mark) => mark.pageIndex === activePage).map((mark, index) => (
                  <button
                    aria-label="Usuń redakcję"
                    className="redaction-mark"
                    key={`${mark.x}-${mark.y}-${index}`}
                    style={{ left: mark.x, top: mark.y, width: mark.width, height: mark.height }}
                    onClick={() => {
                      pushHistory();
                      setRedactions((items) => items.filter((item) => item !== mark));
                    }}
                  >
                    <span>REDAKCJA</span>
                  </button>
                ))}
                {signatures.filter((mark) => mark.pageIndex === activePage).map((mark, index) => (
                  <div className="signature-mark" key={`${mark.text}-${index}`} style={{ left: mark.x, top: mark.y }}>{mark.text}</div>
                ))}
              </article>
            </div>
          )}
          {dragging && <div className="drop-overlay"><Upload size={38} /><strong>{t("drop")}</strong><span>{t("dropDetails")}</span></div>}
        </section>

        <aside
          key={`inspector-${editorAttention}`}
          className={`inspector ${selectedBlock && activeTool === "text" ? "inspector-attention" : ""}`}
        >
          {activeTool === "organize" ? (
            <OrganizePanel
              page={activePage}
              pages={visiblePages.length}
              rotation={rotations[activePage] ?? 0}
              onRotate={rotateActivePage}
              onDelete={() => {
                if (visiblePages.length <= 1) return setNotice(t("pagesInDoc", { pages: 1 }));
                pushHistory();
                setDeletedPages((items) => [...items, activePage]);
                setActivePage(visiblePages.find((page) => page.index !== activePage)?.index ?? 0);
                setSelectedBlockId(null);
                setNotice(t("applyOnSave"));
              }}
              t={t}
            />
          ) : (
            <TypographyInspector key={selectedBlock?.id ?? "no-selection"} selected={selectedBlock} confidence={confidence} onChange={commitBlockChange} onPickFile={() => setNotice(t("uploadFont"))} t={t} />
          )}
        </aside>
      </section>

      {signatureDialog && (
        <div className="modal-backdrop" onMouseDown={() => setSignatureDialog(false)}>
          <div className="signature-dialog" onMouseDown={(event) => event.stopPropagation()}>
            <button className="modal-close" onClick={() => setSignatureDialog(false)} aria-label="Zamknij"><X size={19} /></button>
            <span className="dialog-icon"><Signature size={22} /></span>
            <small>{t("sign").toUpperCase()}</small>
            <h2>{t("addSignature")}</h2>
            <p>{t("signatureCopy")}</p>
            <input value={signatureText} onChange={(event) => setSignatureText(event.target.value)} placeholder="Dawid Kowalski" autoFocus />
            <div className="signature-preview">{signatureText || t("yourSignature")}</div>
            <button className="primary-action" onClick={addSignature}>{t("placeOnPage", { page: activePage + 1 })}</button>
          </div>
        </div>
      )}
    </main>
    <SiteFooter language={language} />
    </>
  );
}

function TypographyInspector({
  selected,
  confidence,
  onChange,
  onPickFile,
  t,
}: {
  selected: TextBlock | null;
  confidence: number;
  onChange: (block: TextBlock, patch: Partial<Pick<TextBlock, "text" | "fontSize" | "fontFamily" | "color">>) => void;
  onPickFile: () => void;
  t: Translate;
}) {
  const [draft, setDraft] = useState(selected?.text ?? "");
  const family = selected?.fontFamily ?? "Inter";
  const size = Math.round(selected?.fontSize ?? 14);

  return (
    <>
      <div className="inspector-title"><div><span className="icon-square"><Type size={18} /></span><span><small>{t("properties")}</small>{t("typography")}</span></div>{selected && <span className="selection-check"><Check size={12} /> {t("selected")}</span>}</div>
      <div className="inspector-section text-edit-section">
        {selected && <div className="edit-attention-label" role="status"><Sparkles size={15} /><span>{t("content")} · {t("selected")}</span></div>}
        <label>{t("content")}</label>
        <textarea value={draft} disabled={!selected} onChange={(event) => setDraft(event.target.value)} onBlur={() => selected && onChange(selected, { text: draft })} placeholder={t("clickText")} />
      </div>
      <div className="inspector-section">
        <label>{t("font")}</label>
        <div className="two-cols">
          <select className="select-control" value={family} disabled={!selected} onChange={(event) => selected && onChange(selected, { fontFamily: event.target.value })}>
            <option value={family}>{cleanFontName(family)}</option>
            <option value="Helvetica">Helvetica</option>
            <option value="Times New Roman">Times New Roman</option>
            <option value="Courier New">Courier New</option>
          </select>
          <div className="stepper">
            <button disabled={!selected} onClick={() => selected && onChange(selected, { fontSize: Math.max(6, selected.fontSize - 1) })}>−</button>
            <span>{size}px</span>
            <button disabled={!selected} onClick={() => selected && onChange(selected, { fontSize: Math.min(96, selected.fontSize + 1) })}>+</button>
          </div>
        </div>
        <div className="match-row"><span>{t("fontMatch")}</span><strong>{selected ? confidence : "—"}{selected ? "%" : ""}</strong></div>
        <div className="match-track"><i style={{ width: selected ? `${confidence}%` : "0%" }} /></div>
        <p className="match-note"><Sparkles size={14} /> {selected ? t("detected", { font: cleanFontName(selected.detectedFont) }) : t("detectHint")}</p>
      </div>
      <div className="inspector-section">
        <label>{t("style")}</label>
        <div className="segmented"><button className="selected">B</button><button><em>I</em></button><button><u>U</u></button><button><s>S</s></button></div>
        <label>{t("alignment")}</label>
        <div className="segmented icons"><button className="selected"><AlignLeft /></button><button><AlignCenter /></button><button><AlignRight /></button><button><AlignJustify /></button></div>
      </div>
      <div className="inspector-section">
        <label htmlFor="pdfix-text-color">{t("color")}</label>
        <div className="color-control">
          <input
            id="pdfix-text-color"
            type="color"
            value={selected?.color ?? "#111827"}
            disabled={!selected}
            onChange={(event) => selected && onChange(selected, { color: event.target.value.toUpperCase() })}
            aria-label={t("color")}
          />
          <b>{selected?.color.toUpperCase() ?? "#111827"}</b>
        </div>
      </div>
      <div className="inspector-actions">
        <button className="secondary-action" onClick={onPickFile}><Upload size={16} /> {t("uploadFont")}</button>
        <button className="primary-action" disabled={!selected} onClick={() => selected && onChange(selected, { text: draft })}>{t("apply")}</button>
      </div>
      <div className="engine-note"><ShieldCheck size={16} /><span><strong>{t("localProcessing")}</strong>{t("localDetails")}</span></div>
    </>
  );
}

function OrganizePanel({
  page,
  pages,
  rotation,
  onRotate,
  onDelete,
  t,
}: {
  page: number;
  pages: number;
  rotation: number;
  onRotate: () => void;
  onDelete: () => void;
  t: Translate;
}) {
  return (
    <>
      <div className="inspector-title"><div><span className="icon-square"><Layers3 size={18} /></span><span><small>{t("document")}</small>{t("organizePages")}</span></div></div>
      <div className="organize-summary"><strong>{t("page", { page: page + 1 })}</strong><span>{t("pagesInDoc", { pages })}</span></div>
      <div className="inspector-section organize-actions">
        <button onClick={onRotate}><RotateCw size={19} /><span><strong>{t("rotate")}</strong>{t("current", { rotation })}</span></button>
        <button className="danger" onClick={onDelete}><Trash2 size={19} /><span><strong>{t("delete")}</strong>{t("applyOnSave")}</span></button>
      </div>
      <div className="engine-note"><FileText size={16} /><span><strong>{t("noQualityLoss")}</strong>{t("untouched")}</span></div>
    </>
  );
}

function DemoThumbnail() {
  return (
    <div className="thumbnail">
      <div className="thumb-title">FAKTURA VAT</div>
      <div className="thumb-line w80" /><div className="thumb-line w55" />
      <div className="thumb-grid" />
      <div className="thumb-line w70" /><div className="thumb-line w90" />
    </div>
  );
}

function BlankThumbnail() {
  return <div className="thumbnail blank-thumbnail"><FilePlus2 size={22} /></div>;
}

function DemoInvoice({ zoom, onOpen, t }: { zoom: number; onOpen: () => void; t: Translate }) {
  return (
    <article className="pdf-page demo-page" style={{ transform: `scale(${zoom / 100})` }}>
      <div className="demo-open-card">
        <span><FileText size={24} /></span>
        <div><strong>{t("openDoc")}</strong><small>{t("openDocCopy")}</small></div>
        <button onClick={onOpen}><Upload size={16} /> {t("choosePdf")}</button>
      </div>
      <div className="invoice-head"><div><span className="eyebrow">DOKUMENT SPRZEDAŻY</span><h1>FAKTURA VAT</h1></div><dl><div><dt>Numer faktury:</dt><dd>FV/07/2026/0156</dd></div><div><dt>Data wystawienia:</dt><dd>05.07.2026</dd></div></dl></div>
      <div className="invoice-rule" />
      <div className="parties"><div className="editable-block selected-block"><span className="block-label">Demo edycji tekstu</span><h2>Sprzedawca</h2><p>PDFix Studio Sp. z o.o.<br />ul. Prosta 12<br />00-001 Warszawa<br />NIP: 525-267-89-33</p><i className="handle top-left" /><i className="handle top-right" /><i className="handle bottom-left" /><i className="handle bottom-right" /></div><div><h2>Nabywca</h2><p>Kreatywne Rozwiązania Sp. z o.o.<br />ul. Długa 45<br />30-001 Kraków<br />NIP: 676-245-67-89</p></div></div>
      <table><thead><tr><th>Nazwa towaru / usługi</th><th>Ilość</th><th>Cena netto</th><th>VAT</th><th>Razem</th></tr></thead><tbody><tr><td>Licencja PDFix Studio</td><td>2</td><td>350,00 zł</td><td>23%</td><td>861,00 zł</td></tr><tr><td>Wsparcie techniczne</td><td>1</td><td>500,00 zł</td><td>23%</td><td>615,00 zł</td></tr><tr><td>Szkolenie online</td><td>1</td><td>800,00 zł</td><td>23%</td><td>984,00 zł</td></tr></tbody></table>
      <div className="summary"><div><span>Suma netto</span><strong>1 650,00 zł</strong></div><div><span>Kwota VAT (23%)</span><strong>379,50 zł</strong></div><div className="total"><span>Razem do zapłaty</span><strong>2 029,50 zł</strong></div></div>
      <footer className="invoice-footer"><span>Słownie: dwa tysiące dwadzieścia dziewięć złotych 50/100</span><span>Dziękujemy za współpracę!</span></footer>
    </article>
  );
}
